import React, { useEffect, useMemo, useRef, useState } from 'react';
import 'rsuite/dist/rsuite.min.css';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  ColumnFiltersState,
  SortingState,
} from '@tanstack/react-table';
import { GuestGroup } from '../types/interfaces';
import { groupNeedsBus } from '../utils/needsBus';

type SortMode = 'alphabetical' | 'lastUpdated';

type YoungLabel = 'Yes' | 'No';
type GenderLabel = 'M' | 'F' | '-';
type ResponseLabel = 'Yes' | 'No' | 'Not Answered';

interface TableRow {
  group: GuestGroup;
  groupName: string;
  guestNames: string[];
  youngStatuses: YoungLabel[];
  genders: GenderLabel[];
  responses: ResponseLabel[];
  side: string;
  location: string;
  probability: string;
  /** Sort key: 0 = all coming, 1 = some coming, 2 = all not coming, 3 = no responses */
  responseSort: number;
  bus: string;
  rsvpCode: string;
  updatedAt: number;
}

/** A row, plus the indices of the guests that pass the active per-guest filters. */
interface DisplayRow extends TableRow {
  visibleGuestIndices: number[];
}

function deriveGroupValue(group: GuestGroup, field: 'side' | 'location' | 'probability'): string {
  const first = group.guests.find(g => g[field]);
  return (first?.[field] as string) || '-';
}

function guestResponse(coming: boolean | undefined): ResponseLabel {
  if (coming === true) return 'Yes';
  if (coming === false) return 'No';
  return 'Not Answered';
}

function buildRows(groups: GuestGroup[]): TableRow[] {
  return groups.map(group => {
    const responses = group.guests.map(g => guestResponse(g.coming));
    const allYes = responses.length > 0 && responses.every(r => r === 'Yes');
    const allNo = responses.length > 0 && responses.every(r => r === 'No');
    const anyYes = responses.some(r => r === 'Yes');
    let responseSort: number;
    if (allYes) responseSort = 0;
    else if (anyYes) responseSort = 1;
    else if (allNo) responseSort = 2;
    else responseSort = 3;

    const responded = group.guests.some(g => g.coming !== undefined);
    const needsBus = groupNeedsBus(group);
    return {
      group,
      groupName: group.groupInvite,
      guestNames: group.guests.map(g => g.fullName),
      youngStatuses: group.guests.map(g => (g.young ? 'Yes' : 'No') as YoungLabel),
      genders: group.guests.map(
        g => (g.gender === 'M' || g.gender === 'F' ? g.gender : '-') as GenderLabel,
      ),
      responses,
      responseSort,
      side: deriveGroupValue(group, 'side'),
      location: deriveGroupValue(group, 'location'),
      probability: deriveGroupValue(group, 'probability'),
      bus: responded ? (needsBus === undefined ? '-' : needsBus ? 'Yes' : 'No') : '-',
      rsvpCode: group.rsvpCode || '',
      updatedAt: group.updatedAt ? new Date(group.updatedAt).getTime() : 0,
    };
  });
}

const columnHelper = createColumnHelper<DisplayRow>();

const RESPONSE_FILTER_ORDER: ResponseLabel[] = ['Yes', 'No', 'Not Answered'];
const YOUNG_FILTER_ORDER: YoungLabel[] = ['Yes', 'No'];
const GENDER_FILTER_ORDER: GenderLabel[] = ['M', 'F'];

/** w-px + nowrap shrink-wraps column; min-w-* lets flexible columns grow. */
const COLUMN_LAYOUT: Record<string, string> = {
  groupName: 'min-w-[9rem]',
  guestNames: 'min-w-[10rem]',
  youngStatuses: 'w-px whitespace-nowrap',
  genders: 'w-px whitespace-nowrap',
  side: 'w-px whitespace-nowrap',
  location: 'w-px whitespace-nowrap',
  probability: 'w-px whitespace-nowrap',
  responses: 'w-px whitespace-nowrap',
  bus: 'w-px whitespace-nowrap',
  rsvpCode: 'min-w-[7rem]',
};

const RSVP_COL_IDS = new Set(['rsvpCode', 'responses', 'bus']);

function getFilterValues(columnFilters: ColumnFiltersState, id: string): string[] | undefined {
  const value = columnFilters.find(f => f.id === id)?.value;
  if (!value || !Array.isArray(value) || value.length === 0) return undefined;
  return value as string[];
}

function MultiColumnFilter({
  column,
  rows,
  fixedOptions,
}: {
  column: { id: string; getFilterValue: () => unknown; setFilterValue: (v: undefined | string[]) => void };
  rows: TableRow[];
  fixedOptions?: readonly string[];
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const options = useMemo(() => {
    if (fixedOptions) return [...fixedOptions];
    const vals = new Set<string>();
    rows.forEach(row => {
      const val = row[column.id as keyof TableRow];
      if (typeof val === 'string' && val && val !== '-') vals.add(val);
    });
    return [...vals].sort();
  }, [rows, column.id, fixedOptions]);

  const selected = (column.getFilterValue() as string[] | undefined) ?? [];

  const toggle = (val: string) => {
    const next = selected.includes(val)
      ? selected.filter(s => s !== val)
      : [...selected, val];
    column.setFilterValue(next.length ? next : undefined);
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  if (options.length <= 1) return null;

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] leading-none border transition-colors ${
          selected.length > 0
            ? 'bg-gray-700 text-white border-gray-700'
            : 'bg-white text-gray-500 border-gray-300 hover:border-gray-400 hover:text-gray-700'
        }`}
        aria-label="Filter column"
        aria-expanded={open}
      >
        ▾
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 min-w-[9rem] max-h-40 overflow-y-auto border border-gray-200 rounded-md bg-white p-2 space-y-1.5 shadow-lg">
          {options.map(opt => (
            <label
              key={opt}
              className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer whitespace-nowrap hover:bg-gray-50 rounded px-0.5"
            >
              <input
                type="checkbox"
                className="rounded border-gray-300 shrink-0"
                checked={selected.includes(opt)}
                onChange={() => toggle(opt)}
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

interface GuestTableProps {
  guestGroups: GuestGroup[];
}

const GuestTable: React.FC<GuestTableProps> = ({ guestGroups }) => {
  const [sortMode, setSortMode] = useState<SortMode>('lastUpdated');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const data = useMemo(() => buildRows(guestGroups), [guestGroups]);

  // External filtering: apply group-level filters to entire rows, and per-guest
  // filters to individual guests within a row. A row is dropped if no guests
  // remain after per-guest filtering.
  const filteredData = useMemo<DisplayRow[]>(() => {
    const sideFilter = getFilterValues(columnFilters, 'side');
    const locationFilter = getFilterValues(columnFilters, 'location');
    const probabilityFilter = getFilterValues(columnFilters, 'probability');
    const busFilter = getFilterValues(columnFilters, 'bus');

    const youngFilter = getFilterValues(columnFilters, 'youngStatuses');
    const genderFilter = getFilterValues(columnFilters, 'genders');
    const responseFilter = getFilterValues(columnFilters, 'responses');

    const result: DisplayRow[] = [];
    for (const row of data) {
      if (sideFilter && !sideFilter.includes(row.side)) continue;
      if (locationFilter && !locationFilter.includes(row.location)) continue;
      if (probabilityFilter && !probabilityFilter.includes(row.probability)) continue;
      if (busFilter && !busFilter.includes(row.bus)) continue;

      const visible: number[] = [];
      for (let i = 0; i < row.guestNames.length; i++) {
        if (youngFilter && !youngFilter.includes(row.youngStatuses[i])) continue;
        if (genderFilter && !genderFilter.includes(row.genders[i])) continue;
        if (responseFilter && !responseFilter.includes(row.responses[i])) continue;
        visible.push(i);
      }
      if (visible.length === 0) continue;
      result.push({ ...row, visibleGuestIndices: visible });
    }
    return result;
  }, [data, columnFilters]);

  const sorting = useMemo<SortingState>(() => {
    if (sortMode === 'lastUpdated') {
      return [
        { id: 'responses', desc: true },
        { id: 'updatedAt', desc: true },
      ];
    }
    return [
      { id: 'responses', desc: true },
      { id: 'groupName', desc: false },
    ];
  }, [sortMode]);

  const columns = useMemo(() => [
    columnHelper.accessor('groupName', {
      header: 'Group Name',
      sortingFn: 'alphanumeric',
      enableColumnFilter: false,
    }),
    columnHelper.accessor('guestNames', {
      header: 'Guests',
      enableSorting: false,
      enableColumnFilter: false,
      cell: info => {
        const visible = info.row.original.visibleGuestIndices;
        const names = info.getValue();
        return (
          <ul className="list-disc list-inside">
            {visible.map(i => (
              <li key={i} className="text-sm">{names[i]}</li>
            ))}
          </ul>
        );
      },
    }),
    columnHelper.accessor('youngStatuses', {
      header: 'Young',
      enableSorting: false,
      cell: info => {
        const visible = info.row.original.visibleGuestIndices;
        const labels = info.getValue();
        return (
          <ul className="list-none space-y-0.5">
            {visible.map(i => (
              <li
                key={i}
                className={`text-sm font-medium ${
                  labels[i] === 'Yes' ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {labels[i]}
              </li>
            ))}
          </ul>
        );
      },
    }),
    columnHelper.accessor('genders', {
      header: 'Gender',
      enableSorting: false,
      cell: info => {
        const visible = info.row.original.visibleGuestIndices;
        const labels = info.getValue();
        return (
          <ul className="list-none space-y-0.5">
            {visible.map(i => (
              <li
                key={i}
                className={`text-sm font-medium ${
                  labels[i] === 'M' ? 'text-sky-600' : labels[i] === 'F' ? 'text-pink-600' : 'text-gray-400'
                }`}
              >
                {labels[i]}
              </li>
            ))}
          </ul>
        );
      },
    }),
    columnHelper.accessor('side', {
      header: 'Side',
    }),
    columnHelper.accessor('location', {
      header: 'Location',
    }),
    columnHelper.accessor('probability', {
      header: 'Probability',
    }),
    columnHelper.accessor('responses', {
      header: 'Response',
      enableSorting: true,
      sortingFn: (a, b) => a.original.responseSort - b.original.responseSort,
      cell: info => {
        const visible = info.row.original.visibleGuestIndices;
        const labels = info.getValue();
        return (
          <ul className="list-none space-y-0.5">
            {visible.map(i => {
              const r = labels[i];
              const colorClass =
                r === 'Yes' ? 'text-green-600'
                : r === 'No' ? 'text-red-600'
                : 'text-gray-400';
              return (
                <li key={i} className={`text-sm font-medium ${colorClass}`}>
                  {r}
                </li>
              );
            })}
          </ul>
        );
      },
    }),
    columnHelper.accessor('bus', {
      header: 'Bus',
      cell: info => {
        const val = info.getValue();
        if (val === '-') return <span className="text-gray-400">-</span>;
        if (val === 'Yes') return <span className="text-green-700 font-medium">Yes</span>;
        return <span className="text-gray-600">No</span>;
      },
    }),
    columnHelper.accessor('rsvpCode', {
      header: 'RSVP Link',
      enableSorting: false,
      enableColumnFilter: false,
      cell: info => {
        const code = info.getValue();
        return code ? (
          <a
            href={`/?code=${code}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800"
          >
            /?code={code}
          </a>
        ) : (
          <span className="text-gray-400">-</span>
        );
      },
    }),
    columnHelper.accessor('updatedAt', {
      header: () => null,
      cell: () => null,
      enableColumnFilter: false,
    }),
  ], []);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { sorting, columnFilters },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: false,
  });

  const activeFilterCount = columnFilters.filter(f => {
    const v = f.value;
    if (v === undefined || v === null) return false;
    if (Array.isArray(v)) return v.length > 0;
    return true;
  }).length;

  const visibleRows = table.getRowModel().rows;
  const visibleGroupCount = visibleRows.length;
  const visiblePeopleCount = visibleRows.reduce(
    (sum, r) => sum + r.original.visibleGuestIndices.length,
    0,
  );
  const totalGroupCount = data.length;
  const totalPeopleCount = data.reduce((sum, r) => sum + r.guestNames.length, 0);

  const countDisplay =
    visibleGroupCount === totalGroupCount && visiblePeopleCount === totalPeopleCount
      ? `${totalGroupCount} groups · ${totalPeopleCount} people`
      : `Showing ${visibleGroupCount} of ${totalGroupCount} groups · ${visiblePeopleCount} of ${totalPeopleCount} people`;

  return (
    <div className="w-full">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm text-gray-600 font-medium">Sort by:</span>
        <button
          onClick={() => setSortMode('alphabetical')}
          className={`text-sm px-3 py-1.5 rounded border cursor-pointer transition-colors duration-200 ${
            sortMode === 'alphabetical'
              ? 'bg-gray-800 text-white border-gray-800'
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'
          }`}
        >
          A → Z
        </button>
        <button
          onClick={() => setSortMode('lastUpdated')}
          className={`text-sm px-3 py-1.5 rounded border cursor-pointer transition-colors duration-200 ${
            sortMode === 'lastUpdated'
              ? 'bg-gray-800 text-white border-gray-800'
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'
          }`}
        >
          Last RSVP'd
        </button>
        {activeFilterCount > 0 && (
          <button
            onClick={() => setColumnFilters([])}
            className="text-sm px-3 py-1.5 rounded border cursor-pointer border-red-300 text-red-600 hover:bg-red-50 transition-colors duration-200 ml-auto"
          >
            Clear filters ({activeFilterCount})
          </button>
        )}
      </div>
      <div className="text-sm text-gray-500 mb-3">{countDisplay}</div>
      <div className="overflow-x-auto">
        <table className="w-full table-auto border-collapse bg-white border border-gray-200">
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id} className="bg-gray-50">
                {headerGroup.headers.map(header => {
                  if (header.id === 'updatedAt') return null;
                  const isRsvpCol = RSVP_COL_IDS.has(header.id);
                  const colLayout = COLUMN_LAYOUT[header.id] ?? '';
                  const title = flexRender(header.column.columnDef.header, header.getContext());
                  let fixedOptions: readonly string[] | undefined;
                  if (header.column.id === 'responses') fixedOptions = RESPONSE_FILTER_ORDER;
                  else if (header.column.id === 'youngStatuses') fixedOptions = YOUNG_FILTER_ORDER;
                  else if (header.column.id === 'genders') fixedOptions = GENDER_FILTER_ORDER;
                  return (
                    <th
                      key={header.id}
                      className={`relative h-11 px-3 py-2 text-left text-sm font-semibold text-gray-700 border-b border-gray-200 align-middle ${colLayout} ${isRsvpCol ? 'bg-blue-50' : ''}`}
                    >
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="truncate">{title}</span>
                        {header.column.getCanFilter() && (
                          <MultiColumnFilter
                            column={header.column}
                            rows={data}
                            fixedOptions={fixedOptions}
                          />
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {visibleRows.map(row => (
              <tr key={row.id} className="hover:bg-gray-50">
                {row.getVisibleCells().map(cell => {
                  if (cell.column.id === 'updatedAt') return null;
                  const isRsvpCol = RSVP_COL_IDS.has(cell.column.id);
                  const colLayout = COLUMN_LAYOUT[cell.column.id] ?? '';
                  return (
                    <td
                      key={cell.id}
                      className={`px-3 py-2 border-b border-gray-200 text-left text-sm align-top ${colLayout} ${isRsvpCol ? 'bg-blue-50' : ''}`}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GuestTable;
